import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Sparkles } from 'lucide-react';
import { useSettings } from '../lib/usePublicData';

const STORAGE_PREFIX = 'zz_win_sticker_dismissed_';

/**
 * Floating dismissable Scan & Win sticker.
 * - Bottom-right fixed position on all public routes except /win and /admin/*.
 * - Click body → navigate to /win.
 * - Click × → store dismiss flag in localStorage under the campaign version.
 *   Admin can bump `win_campaign_sticker_version` to force re-show for all users.
 * - Hidden when: campaign disabled, sticker disabled, dismissed for current version,
 *   on /win, on /admin/*.
 * - Respects prefers-reduced-motion.
 */
const FloatingWinSticker = () => {
    const { data: settings } = useSettings();
    const navigate = useNavigate();
    const location = useLocation();
    const [dismissed, setDismissed] = useState(true); // start hidden — toon enkel zodra checks slagen

    const version = (settings?.win_campaign_sticker_version || 'v1').trim() || 'v1';
    const storageKey = `${STORAGE_PREFIX}${version}`;
    const stickerText = settings?.win_campaign_sticker_text || 'WIN €20 AAN DRANKBONNEN';

    // Initialiseer dismiss-state vanuit localStorage zodra de versie bekend is
    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            setDismissed(raw === '1');
        } catch {
            setDismissed(false);
        }
    }, [storageKey]);

    const handleClick = useCallback(() => {
        navigate('/win');
    }, [navigate]);

    const handleDismiss = useCallback((e) => {
        e.stopPropagation();
        try { window.localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
        setDismissed(true);
    }, [storageKey]);

    // Route filters
    const path = location.pathname || '/';
    const onWinPage = path === '/win' || path.startsWith('/win/');
    const onAdmin = path.startsWith('/admin');

    if (!settings) return null;
    if (!settings.win_campaign_enabled) return null;
    if (!settings.win_campaign_sticker_enabled) return null;
    if (dismissed) return null;
    if (onWinPage || onAdmin) return null;

    return (
        <div
            data-testid="floating-win-sticker"
            role="button"
            tabIndex={0}
            aria-label={`${stickerText} — open de actie`}
            onClick={handleClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
            className="fixed z-50 bottom-4 right-4 sm:bottom-5 sm:right-5 zz-win-floating zz-win-floating-anim cursor-pointer select-none outline-none focus-visible:ring-2 focus-visible:ring-[var(--green-dark)] focus-visible:ring-offset-2"
            style={{
                backgroundColor: 'var(--cream)',
                border: '3px solid var(--green)',
                color: 'var(--green)',
                borderRadius: '14px',
                padding: '10px 36px 10px 12px',
                maxWidth: 'min(82vw, 240px)',
                fontFamily: 'Bungee, system-ui, sans-serif',
                fontSize: '12px',
                lineHeight: 1.15,
                letterSpacing: '0.02em',
                boxShadow: '0 4px 0 var(--green-dark), 0 8px 18px rgba(0, 107, 68, 0.25)',
            }}
        >
            <span className="flex items-center gap-1.5">
                <Sparkles size={14} aria-hidden="true" />
                <span className="break-words">{stickerText}</span>
            </span>
            <button
                type="button"
                onClick={handleDismiss}
                data-testid="floating-win-sticker-dismiss"
                aria-label="Verberg deze melding"
                className="absolute top-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full text-[var(--green-dark)] hover:bg-[var(--green)]/15 transition-colors"
            >
                <X size={14} aria-hidden="true" />
            </button>
        </div>
    );
};

export default FloatingWinSticker;
